import React, { useState, useEffect } from 'react';
import { Contact } from './types';
import { loadContactsData } from './contactsData';
import { saveContactList } from './api';
import './ContactEditor.css';

const ContactEditor: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [nextId, setNextId] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<'all' | 'owner' | 'vet' | 'staff'>('all');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(true);

  // Load contacts on component mount
  useEffect(() => {
    loadContactsData()
      .then(({ contacts, nextId }) => {
        setContacts(contacts);
        setNextId(nextId);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load contacts:', error);
        alert('Fehler beim Laden der Kontakte');
        setIsLoading(false);
      });
  }, []);

  const getEmptyContact = (): Contact => ({
    id: `contact/${nextId}`,
    name: '',
    phone: '',
    category: 'staff',
    notes: ''
  });

  const handleAdd = () => {
    setEditingContact(getEmptyContact());
    setIsAdding(true);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact({ ...contact });
    setIsAdding(false);
  };

  const handleDelete = (contactId: string) => {
    if (window.confirm('Möchten Sie diesen Kontakt wirklich löschen?')) {
      const updatedContacts = contacts.filter(c => c.id !== contactId);
      setContacts(updatedContacts);
      saveToServer(updatedContacts);
    }
  };

  const saveToServer = async (updatedContacts: Contact[]): Promise<void> => {
    setSaveStatus('saving');
    try {
      const data = {
        contactNextID: nextId,
        contacts: updatedContacts.reduce((acc, contact) => {
          acc[contact.id] = contact;
          return acc;
        }, {} as Record<string, Contact>)
      };

      await saveContactList(data);

      console.log('Save successful');
      setSaveStatus('saved');
      // Wait 2 seconds to show success message
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
    } catch (error: any) {
      console.error('Error saving:', error);
      setSaveStatus('error');
      alert(`Fehler beim Speichern:\n\n${error.message}\n\nDetails siehe Browser-Konsole (F12)`);
      // Wait 3 seconds to show error message
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveStatus('idle');
      throw error; // Re-throw so handleSave knows it failed
    }
  };

  const handleSave = async () => {
    if (!editingContact) return;

    if (!editingContact.name.trim()) {
      alert('Bitte geben Sie einen Namen ein.');
      return;
    }

    let updatedContacts: Contact[];
    if (isAdding) {
      updatedContacts = [...contacts, editingContact];
      setContacts(updatedContacts);
      setNextId(nextId + 1);
    } else {
      updatedContacts = contacts.map(c => c.id === editingContact.id ? editingContact : c);
      setContacts(updatedContacts);
    }

    // Save to server and wait for completion (including status display delay)
    try {
      await saveToServer(updatedContacts);
      // Only close modal after successful save and delay
      setEditingContact(null);
      setIsAdding(false);
    } catch (error) {
      // Keep modal open on error so user can retry
      console.error('Save failed, keeping modal open');
    }
  };

  const handleCancel = () => {
    setEditingContact(null);
    setIsAdding(false);
  };

  const handleInputChange = (field: keyof Contact, value: any) => {
    if (editingContact) {
      setEditingContact({
        ...editingContact,
        [field]: value
      });
    }
  };

  const handleExportJSON = () => {
    const data = {
      contactNextID: nextId,
      contacts: contacts.reduce((acc, contact) => {
        acc[contact.id] = contact;
        return acc;
      }, {} as Record<string, Contact>)
    };

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ContactList.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'owner': return 'Pferdebesitzer';
      case 'vet': return 'Tierarzt';
      case 'staff': return 'Personal';
      default: return category;
    }
  };

  const filteredContacts = contacts
    .filter(contact => filterCategory === 'all' || contact.category === filterCategory)
    .filter(contact =>
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.notes && contact.notes.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (isLoading) {
    return (
      <div className="contact-editor">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Lade Kontakte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-editor">
      <header className="header">
        <h2>Kontakt Editor</h2>
        <h4>
          <strong>{filteredContacts.length}</strong> {filteredContacts.length === 1 ? 'Kontakt' : 'Kontakte'} sichtbar
        </h4>
      </header>

      <div className="editor-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Suche nach Name, Telefon oder Notiz..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="control-buttons">
          <label>
            Kategorie:
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value as any)}>
              <option value="all">Alle</option>
              <option value="owner">Pferdebesitzer</option>
              <option value="vet">Tierarzt</option>
              <option value="staff">Personal</option>
            </select>
          </label>
          <button onClick={handleAdd} className="btn-add">
            Neuer Kontakt
          </button>
          <button onClick={handleExportJSON} className="btn-export">
            Als JSON exportieren
          </button>
        </div>
      </div>

      {editingContact && (
        <>
          <div className="modal-overlay" onClick={handleCancel}></div>
          <div className="modal-dialog">
            <h3>{isAdding ? 'Neuer Kontakt' : 'Kontakt bearbeiten'}</h3>
            {saveStatus !== 'idle' && (
              <div className={`save-status-modal save-status-${saveStatus}`}>
                {saveStatus === 'saving' && '💾 Speichert...'}
                {saveStatus === 'saved' && '✓ Gespeichert!'}
                {saveStatus === 'error' && '⚠ Fehler beim Speichern'}
              </div>
            )}
            <div className="form-grid">
              <div className="form-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={editingContact.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Telefon</label>
                <input
                  type="text"
                  value={editingContact.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Kategorie</label>
                <select
                  value={editingContact.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="owner">Pferdebesitzer</option>
                  <option value="vet">Tierarzt</option>
                  <option value="staff">Personal</option>
                </select>
              </div>
              <div className="form-field full-width">
                <label>Notizen</label>
                <textarea
                  value={editingContact.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <div className="form-buttons">
              <button onClick={handleSave} className="btn-save">
                Speichern
              </button>
              <button onClick={handleCancel} className="btn-cancel">
                Abbrechen
              </button>
            </div>
          </div>
        </>
      )}

      <div className="contact-table-container">
        <table className="contact-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Telefon</th>
              <th>Kategorie</th>
              <th>Notizen</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.map((contact) => (
              <tr key={contact.id}>
                <td>{contact.name}</td>
                <td>
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="phone-link">
                      {contact.phone}
                    </a>
                  )}
                </td>
                <td>{getCategoryLabel(contact.category)}</td>
                <td>{contact.notes || '-'}</td>
                <td className="action-buttons">
                  <button onClick={() => handleEdit(contact)} className="btn-edit">
                    Bearbeiten
                  </button>
                  <button onClick={() => handleDelete(contact.id)} className="btn-delete">
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactEditor;
